import React, { useEffect, useState } from 'react';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import FBOTitleBar from 'ui/theme/components/FBOTitleBar/FBOTitleBar';
import FBOMenuButton from 'ui/theme/components/FBOMenuButton/FBOMenuButton';
import {
  TRANSFER_ORDER_COLUMNS,
  createTransferOrderActionBarOptions,
  TransferOrderPageAction,
} from './consts';
import TransferOrderRow from './TransferOrderRow';
import {
  fetchTransferOrdersAPI,
  DisplayTransferOrder,
} from 'services/transferOrders';
import { ItemsTable } from 'ui/components/Table/ItemsTable';
import { PermissionType } from 'services/permissions';
import { PaginationDirection } from 'services/url';
import { logErrorCtx } from 'app/logging';

export interface TransferOrderSearchResultsProps {
  handleAddNewClick: () => void;
  activeTransferOrderId?: number | null;
  onTransferOrderClick?: (id: number) => void;
}

const TransferOrderSearchResults: React.FC<TransferOrderSearchResultsProps> = ({
  handleAddNewClick,
  activeTransferOrderId,
  onTransferOrderClick,
}) => {
  const [transferOrders, setTransferOrders] = useState<DisplayTransferOrder[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [selectedTransferOrders, setSelectedTransferOrders] = useState<
    number[]
  >([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalRows: 0,
    sort: { sortBy: 'number', direction: PaginationDirection.Ascending },
  });

  const handleTransferOrderPageAction = (action: TransferOrderPageAction) => {
    switch (action) {
      case TransferOrderPageAction.AddTransferOrder:
        handleAddNewClick();
        break;
    }
  };

  const handleSelectClick = (selected: number | number[]) => {
    if (Array.isArray(selected)) {
      setSelectedTransferOrders(selected);
      return;
    }
    setSelectedTransferOrders((prev) =>
      prev.includes(selected)
        ? prev.filter((id) => id !== selected)
        : [...prev, selected]
    );
  };

  const handleAction = () => {
    // Row actions will be implemented when requirements are defined
  };

  const handlePaginationChange = async (newPagination: typeof pagination) => {
    setIsLoading(true);
    setLoadError(null);
    setPagination(newPagination);
    try {
      // Backend pagination will be wired in future iteration
      const response = await fetchTransferOrdersAPI();
      setTransferOrders(response.data);
      setPagination(response.pagination);
    } catch (error) {
      const err = error as Error;
      logErrorCtx('Failed to fetch transfer orders during pagination', {
        stackTrace: err.stack,
        error: err,
        description: `Pagination change failed for page ${newPagination.page}`,
        component: 'TransferOrderSearchResults',
      });
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const response = await fetchTransferOrdersAPI();
        setTransferOrders(response.data);
        setPagination(response.pagination);
      } catch (error: any) {
        const err = error as Error;
        logErrorCtx('Failed to fetch transfer orders on initial load', {
          stackTrace: err.stack,
          error: err,
          description: 'Initial transfer orders data fetch failed',
          component: 'TransferOrderSearchResults',
        });
        setLoadError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <FBOTitleBar title="Transfer Orders">
        <>
          {activeTransferOrderId !== -1 && (
            <FBOButton
              style={{ marginRight: '8px' }}
              variant={!activeTransferOrderId ? 'primary' : 'secondary'}
              size="medium"
              color="positive"
              icon="FBOAddCircle"
              onClick={handleAddNewClick}
              permissions={[PermissionType.TransferOrdersCreate]}
              data-qa="transfer-order-new"
            >
              {!activeTransferOrderId ? 'Add New' : ''}
            </FBOButton>
          )}
          <FBOMenuButton
            variant="secondary"
            items={createTransferOrderActionBarOptions(
              handleTransferOrderPageAction
            )}
            data-qa="transfer-orders-three-dot-menu"
          />
        </>
      </FBOTitleBar>

      <ItemsTable
        data={transferOrders}
        columns={TRANSFER_ORDER_COLUMNS}
        activeId={activeTransferOrderId}
        onItemClick={onTransferOrderClick}
        onSelectedChange={handleSelectClick}
        selectedItems={selectedTransferOrders}
        selectableItems={true}
        RenderCustomRow={TransferOrderRow}
        isLoadingData={isLoading}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        onAction={handleAction}
        dataQa="transfer-orders-table"
        tableLayoutFixed
        emptyTableText={
          loadError
            ? 'Failed to load transfer orders. Please try again.'
            : "Add a new entry by pressing 'Add New'"
        }
        showEnhancedTablePaginationActions={true}
      />
    </>
  );
};

export default TransferOrderSearchResults;
